import type { CustomEventData, EventType, PageViewEventData, UsageEvent } from '@coffee-to-go/common';
import type { Session } from 'better-auth';
import { create } from 'zustand';
import { apiClient } from '@/api/apiClient';
import { config } from '@/config/env';
import { createLogger } from '@/utils/logger';

const logger = createLogger('TrackingStore');

export interface QueuedEvent extends UsageEvent {
  id: string;
}

interface TrackingStore {
  eventQueue: QueuedEvent[];
  isSubmitting: boolean;
  lastFlushTime: number;
  flushTimer: ReturnType<typeof setTimeout> | null;
  flushAbortController: AbortController | null;
  context: {
    pageUrl?: string;
    session?: Session | null;
  };

  // Actions (public)
  actions: {
    trackEvent: (eventType: EventType, eventData?: CustomEventData, pageUrl?: string) => void;
    trackPageView: (eventData?: PageViewEventData, pageUrl?: string) => void;
    flushEvents: () => Promise<void>;
    flushEventsBeacon: () => void;
    setPageUrl: (pageUrl: string) => void;
    setSession: (session: Session | null) => void;
  };
}

// Configuration constants
const BATCH_SIZE_THRESHOLD = 20;
const FLUSH_INTERVAL_MS = 10 * 1000; // 10 seconds (batching cadence)
const TRACKING_RATE_WINDOW_MS = 60 * 1000; // 1 minute (server limiter window)
const TRACKING_RATE_MAX_REQUESTS = 20; // must match trackingLimiter max in API
const MAX_QUEUE_SIZE = 50; // Prevent memory issues
const IS_TRACKING_ENABLED = config.trackingEnabled;

const useTrackingStore = create<TrackingStore>()((set, get) => {
  // Internal helpers (not exposed via state)
  const clearFlushTimerInternal = () => {
    const { flushTimer } = get();
    if (flushTimer) {
      clearTimeout(flushTimer);

      set({ flushTimer: null });
    }
  };

  const scheduleFlushInInternal = (delayMs: number) => {
    const { flushTimer } = get();

    if (flushTimer) {
      clearTimeout(flushTimer);
    }
    const timer = setTimeout(() => void flushEventsInternal(), Math.max(0, delayMs));

    set({ flushTimer: timer });
  };

  const scheduleFlushInternal = () => {
    const { flushTimer } = get();

    if (flushTimer) {
      clearTimeout(flushTimer);
    }
    const timer = setTimeout(() => void flushEventsInternal(), FLUSH_INTERVAL_MS);

    set({ flushTimer: timer });
  };

  const addEventInternal = (event: Omit<UsageEvent, 'page_url' | 'timestamp'>) => {
    const { context } = get();
    const currentUrl = context.pageUrl ?? window.location.pathname + window.location.search;

    const queuedEvent: QueuedEvent = {
      ...event,
      page_url: currentUrl,
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(),
    };

    set((state) => {
      const newQueue = [...state.eventQueue, queuedEvent];
      const trimmedQueue = newQueue.length > MAX_QUEUE_SIZE ? newQueue.slice(-MAX_QUEUE_SIZE) : newQueue;
      return { eventQueue: trimmedQueue };
    });

    const { eventQueue } = get();
    if (eventQueue.length >= BATCH_SIZE_THRESHOLD) {
      void flushEventsInternal();
    } else {
      scheduleFlushInternal();
    }
  };

  const buildTrackingUrl = (): string => {
    const url = new URL('/api/usage/events', config.apiUrl);
    return url.toString();
  };

  const flushEventsWithBeaconInternal = () => {
    const { eventQueue, context } = get();
    if (eventQueue.length === 0 || !context.session) {
      return;
    }

    const { flushAbortController } = get();
    if (flushAbortController) {
      try {
        flushAbortController.abort();
      } catch {}
    }

    clearFlushTimerInternal();

    const eventsToSend = eventQueue.map(({ id, ...event }) => event);

    if (IS_TRACKING_ENABLED) {
      const payload = JSON.stringify({ events: eventsToSend });
      const blob = new Blob([payload], { type: 'application/json' });
      const url = buildTrackingUrl();

      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, blob);
      }
    }

    set({ eventQueue: [] });
  };

  const flushEventsInternal = async () => {
    const { session } = get().context;
    if (!session) {
      return;
    }

    const { eventQueue, isSubmitting, lastFlushTime } = get();

    if (eventQueue.length === 0 || isSubmitting) {
      return;
    }

    const now = Date.now();
    const timeSinceLastFlush = now - lastFlushTime;
    const minSpacingMs = Math.ceil(TRACKING_RATE_WINDOW_MS / TRACKING_RATE_MAX_REQUESTS);

    if (timeSinceLastFlush < minSpacingMs) {
      scheduleFlushInInternal(minSpacingMs - timeSinceLastFlush + 10);
      return;
    }
    clearFlushTimerInternal();

    const controller = new AbortController();
    set({ flushAbortController: controller, isSubmitting: true });

    try {
      const eventsToSend = eventQueue.map(({ id, ...event }) => event);

      if (IS_TRACKING_ENABLED) {
        await apiClient.post('/usage/events', { events: eventsToSend }, { signal: controller.signal });
      }

      set({ eventQueue: [], lastFlushTime: now });
    } catch (error) {
      // Swallow abort errors quietly; other errors are still logged
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        logger.error('Error flushing events', { error });
      }
    } finally {
      set({ isSubmitting: false, flushAbortController: null });
    }
  };

  return {
    // Initial state
    eventQueue: [],
    isSubmitting: false,
    lastFlushTime: 0,
    flushTimer: null,
    flushAbortController: null,
    context: {
      pageUrl: window.location.pathname + window.location.search,
    },

    actions: {
      trackEvent: (eventType, eventData) => {
        addEventInternal({
          event_type: eventType,
          event_data: eventData,
        });
      },

      trackPageView: (eventData) => {
        const pageViewData: PageViewEventData = {
          referrer: document.referrer || undefined,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
          ...eventData,
        };

        addEventInternal({
          event_type: 'page_view',
          event_data: pageViewData,
        });
      },

      flushEvents: flushEventsInternal,
      flushEventsBeacon: flushEventsWithBeaconInternal,
      setPageUrl: (pageUrl: string) => set((state) => ({ context: { ...state.context, pageUrl } })),
      setSession: (session: Session | null) => set((state) => ({ context: { ...state.context, session } })),
    },
  };
});

export const useTrackingActions = () => useTrackingStore((state) => state.actions);

// Test-only accessor for verifying abort behavior in unit tests
export const __test_getFlushAbortController = (): AbortController | null => useTrackingStore.getState().flushAbortController;
